
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatResponse {
  message: string;
  suggestions?: string[];
  success: boolean;
}

interface AIAssistanceRequest {
  query: string;
  context?: string;
  userPreferences?: any;
}

import { apiFetch } from './apiService';

class ChaloAppAPI {
  private baseUrl: string;
  private apiKey: string | null;

  constructor() {
    this.baseUrl = 'https://api.chalo.com'; // Replace with actual API URL
    this.apiKey = localStorage.getItem('chalo_api_key');
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
    localStorage.setItem('chalo_api_key', apiKey);
  }

  async sendChatMessage(message: string, conversationHistory: ChatMessage[] = []): Promise<ChatResponse> {
    try {
      const response = await apiFetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          message,
          history: conversationHistory.slice(-10), // Keep last 10 messages for context
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return {
        message: data.response,
        suggestions: data.suggestions || [],
        success: true,
      };
    } catch (error) {
      console.error('Chat API Error:', error);
      return {
        message: 'Sorry, I\'m having trouble connecting right now. Please try again later.',
        success: false,
      };
    }
  }

  async getAIAssistance(request: AIAssistanceRequest): Promise<ChatResponse> {
    try {
      const response = await apiFetch(`${this.baseUrl}/ai-assistance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          query: request.query,
          context: request.context || '',
          userPreferences: request.userPreferences || {},
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`AI Assistance API Error: ${response.status}`);
      }

      const data = await response.json();
      return {
        message: data.assistance,
        suggestions: data.suggestions || [],
        success: true,
      };
    } catch (error) {
      console.error('AI Assistance API Error:', error);
      return {
        message: 'I\'m unable to provide assistance right now. Please try again later.',
        success: false,
      };
    }
  }

  async getRideRecommendations(fromLocation: string, toLocation: string, preferences: any = {}) {
    try {
      const response = await apiFetch(`${this.baseUrl}/recommendations/rides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          from: fromLocation,
          to: toLocation,
          preferences,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Recommendations API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Ride Recommendations API Error:', error);
      return { rides: [], success: false };
    }
  }

  async getRouteOptimization(waypoints: string[]) {
    try {
      const response = await apiFetch(`${this.baseUrl}/routes/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          waypoints,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Route Optimization API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Route Optimization API Error:', error);
      return { optimizedRoute: [], success: false };
    }
  }
}

export const chaloAPI = new ChaloAppAPI();
export type { ChatMessage, ChatResponse, AIAssistanceRequest };
